<template>
  <Modal title="目標設定" @close="$emit('close')">
    <template #content>
      <div class="main">
        <LoadingIndicator v-if="busy" />
        <template v-else>
          <div class="info">
            <p>月間の目標人数を月単位または曜日単位で設定してください。</p>
            <p class="note">※選択した単位の設定のみが保存されます</p>
          </div>
          <RadioList :items="{ 1: '月単位', 2: '曜日単位' }" v-model="type" class="type" />
          <div class="body">
            <div v-if="isMonth" class="month">
              <div class="layout">
                <p class="title">1ヶ月</p>
                <div class="box">
                  <NumericInput v-model="monthly" defaultVal="0" :maxValue="100000" asInput />
                  <div class="split">
                    <p class="people">
                      <span>{{ numberFormat(people) }}</span> 人／日
                    </p>
                    <p class="note">※当月の営業日を基に算出</p>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="week">
              <ul class="list">
                <li class="layout" v-for="(week, i) in afterWeekly" :key="i">
                  <p class="title">{{ weeks[i] }}曜日</p>
                  <NumericInput
                    :value="week"
                    @update="changeWeekly(i, $event)"
                    defaultVal="0"
                    :maxValue="100000"
                    asInput
                  />
                </li>
              </ul>
              <div class="sum">
                <p class="people">
                  <span>{{ numberFormat(people) }}</span> 人／月
                </p>
                <p class="note">※当月の営業日を基に算出</p>
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>
    <template #footer-center>
      <Button @click="$emit('close')">戻る</Button>
      <Button primary @click="save" :disabled="!isModified">設定</Button>
    </template>
  </Modal>
</template>

<script>
import { numberFormat } from '@/utils/number'
import { getCalendar } from '@/utils/calendar'
import { formatDate, today, splitDate } from '@/utils/date'
import { mapState, mapGetters } from 'vuex'
import { format } from 'date-fns'
import { last, isEqual } from 'lodash'
import Modal from '@/components/molecules/Modal'
import NumericInput from '@/components/molecules/NumericInput'
import LoadingIndicator from '@/components/molecules/LoadingIndicator'
import Button from '@/components/atoms/Button'
import RadioList from '@/components/atoms/RadioList'

export default {
  name: 'TargetSettingModal',
  components: {
    Modal,
    NumericInput,
    LoadingIndicator,
    Button,
    RadioList,
  },
  props: {
    setting: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      afterType: null,
      afterMonthly: null,
      afterWeekly: Array(7).fill(0),
      businessWeekCount: Array(7).fill(0),
    }
  },
  computed: {
    ...mapState({
      businessDay: state => state.analysis.target.businessDay,
      busy: state => state.analysis.target.modalBusy,
    }),
    ...mapGetters({
      weeks: 'analysis/target/weeks',
    }),
    isModified() {
      return (
        this.afterType !== this.setting.type ||
        this.afterMonthly !== this.setting.target?.monthly ||
        !isEqual(this.afterWeekly, this.setting.target.weekly)
      )
    },
    isMonth() {
      return [1, null].includes(this.afterType)
    },
    people() {
      const businessDayCount = this.currentMonthBusinessDayList.length

      // TODO 営業日のない場合の値確認
      return this.isMonth
        ? Math.ceil(businessDayCount ? this.monthly / businessDayCount : this.monthly)
        : this.weekly.reduce((sum, week, i) => {
            return sum + week * this.businessWeekCount[i]
          }, 0)
    },
    month() {
      return splitDate(today()).month
    },
    type: {
      set(type) {
        this.afterType = type
      },
      get() {
        return this.afterType
      },
    },
    monthly: {
      set(monthly) {
        this.afterMonthly = +monthly
      },
      get() {
        return this.afterMonthly
      },
    },
    weekly() {
      return this.afterWeekly
    },
    currentMonthBusinessDayList() {
      if (!this.businessDay) return []
      return this.businessDay.businessdayList[format(today(), 'YYYY-MM')]
    },
  },
  async mounted() {
    await this.$store.dispatch('analysis/target/getBusinessDay')
    this.aggregateBusinessWeekCount()
    this.afterType = this.setting.type ?? 1
    this.afterWeekly = this.setting.target?.weekly ? [...this.setting.target.weekly] : Array(7).fill(0)
    this.afterMonthly = this.setting.target?.monthly ?? 0
  },
  methods: {
    numberFormat,
    changeWeekly(i, e) {
      this.afterWeekly.splice(i, 1, +e)
    },
    async save() {
      await this.$store.dispatch('analysis/target/save', {
        type: this.afterType,
        target: this.afterType === 1 ? [this.afterMonthly] : this.afterWeekly,
      })
      this.$emit('close')
      this.$emit('fetch')
    },
    aggregateBusinessWeekCount() {
      const weeks = getCalendar(this.selectedMonth)
      const calendar = weeks.map(week =>
        week.map(date => {
          const month = formatDate(date, 'M')
          return { month }
        }),
      )

      // 今月の曜日回数カウント
      this.businessWeekCount = last(
        calendar.map(arr =>
          arr.map((o, i) =>
            +o.month === this.month
              ? (this.businessWeekCount[i] = ++this.businessWeekCount[i])
              : this.businessWeekCount[i],
          ),
        ),
      )
    },
  },
}
</script>

<style lang="postcss" scoped>
.main {
  box-sizing: border-box;
  display: grid;
  grid-gap: 24px;
  grid-template-rows: min-content 40px 1fr;
  min-height: 436px;
  overflow-y: scroll;
  padding: 32px;
}

.info {
  letter-spacing: -0.3px;

  .note {
    font-size: 11px;
    line-height: 16px;
  }
}

.type {
  grid-auto-columns: 50% 50%;
  justify-self: center;
  width: 260px;
}

.layout {
  align-self: center;
  display: grid;
  grid-auto-flow: row;
  grid-gap: 10px;

  .title {
    color: var(--very-dark-gray-2);
    font-weight: bold;
    height: 20px;
  }
}

.split,
.sum {
  box-sizing: border-box;
  display: grid;
  grid-auto-flow: row;

  .people {
    color: var(--very-dark-gray-2);
    height: 21px;
    line-height: 1;

    span {
      font-size: 21px;
    }
  }

  .note {
    color: var(--dark-gray);
    font-size: 10px;
  }
}

.month {
  .box {
    display: grid;
    grid-auto-flow: column;
    grid-template-columns: 98px 1fr;
  }

  .split {
    border-left: 1px solid var(--border-color);
    margin-left: 16px;
    padding-left: 14px;
  }
}

.week {
  .list {
    display: grid;
    grid-gap: 22px 16px;
    grid-template-columns: repeat(auto-fill, 92px);
    grid-template-rows: repeat(auto-fill, 66px);
  }

  .sum {
    border-top: 1px solid var(--border-color);
    margin-top: 24px;
    padding-top: 20px;
  }
}
</style>
